<template>
    <div class="container">
        <div class="page-content">
            <div class="row">
                <div class="col-12">
                    <h1 class="page-heading">
                        {{ $t('grading.title') }}
                    </h1>
                </div>

                <div class="col-12 mb-2" v-if="summary">
                    <div class="mb-0">
                        <span style="font-size: 1.2em;" class="me-2">{{ summary.submitted_today }} / {{ summary.limit_per_day }}</span>
                        <small class="muted">{{ $t('grading.limitationInfo') }}</small>
                    </div>
                    <div class="progress rounded">
                        <div class="progress-bar progress-bar-striped bg-info progress-bar-animated rounded" role="progressbar" :aria-valuenow="summary.submitted_today" aria-valuemin="0" :aria-valuemax="summary.limit_per_day" :style="'width: '+(summary.submitted_today / summary.limit_per_day * 100)+'%;'"></div>
                    </div>
                </div>

                <div class="col-12 mb-0" style="white-space: nowrap">
                    <ul class="nav nav-tabs" role="tablist">
                        <li class="nav-item" role="presentation" v-on:click="tab = 'submit'">
                            <h5 class="nav-link active" id="submit-tab" data-bs-toggle="tab" data-bs-target="#tab" type="button" role="tab" aria-controls="home" aria-selected="true">
                                {{ $t('grading.tabs.submit') }}
                            </h5>
                        </li>

                        <li class="nav-item" role="presentation" v-on:click="tab = 'pending'">
                            <h5 class="nav-link" id="pending-tab" data-bs-toggle="tab" data-bs-target="#tab" type="button" role="tab" aria-controls="profile" aria-selected="false">
                                {{ $t('grading.tabs.pending') }}
                            </h5>
                        </li>

                        <li class="nav-item" role="presentation" v-on:click="tab = 'completed'">
                            <h5 class="nav-link" id="completed-tab" data-bs-toggle="tab" data-bs-target="#tab" type="button" role="tab" aria-controls="profile" aria-selected="false">
                                {{ $t('grading.tabs.completed') }}
                            </h5>
                        </li>
                    </ul>
                </div>

                <!-- Submit Tab -->
                <div class="col-12 px-0" v-if="tab === 'submit'">
                    <div class="row">
                        <div class="col-12 col-lg-8">
                            <div class="card bg-black text-white">
                                <div class="card-body">
                                    <div class="mb-2">
                                        <button class="btn btn-light text-dark" @click="openSelectionModal">
                                            {{ $t('grading.selectCards') }}
                                        </button>
                                        <small class="text-muted ms-2">{{ $t('grading.selectionInfo', {count: selection.length, limit: remainingToday}) }}</small>
                                    </div>

                                    <div v-if="selectedCards.length" class="table-responsive">
                                        <table class="table table-dark table-striped align-middle">
                                            <thead>
                                            <tr>
                                                <th style="width: 48px;"></th>
                                                <th>{{ $t('grading.card') }}</th>
                                                <th class="text-end">{{ $t('grading.price') }}</th>
                                                <th class="text-end">{{ $t('grading.duration') }}</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr v-for="uc in selectedCards" :key="uc.card_id">
                                                <td>
                                                    <a href="javascript:" class="btn btn-sm btn-danger" @click="removeFromSelection(uc.card_id)">×</a>
                                                </td>
                                                <td>
                                                    <div class="d-flex align-items-center">
                                                        <div class="me-2 rounded" :style="'width:48px;height:68px;background-size:cover;background-image:url('+CardService.getSmallCardImageUrl({card_id: uc.card_id, set_id: uc.set_id})+')'"></div>
                                                        <div>
                                                            <div class="fw-bold">{{ uc.name }}</div>
                                                            <small class="text-muted">{{ uc.rarity }}</small>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td class="text-end">
                                                    {{ (uc.price||0).toLocaleString() }}
                                                </td>
                                                <td class="text-end">
                                                    {{ (uc.duration_hours || 0) }}h
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <div class="d-flex justify-content-between align-items-center mt-3">
                                        <div>
                                            <small class="text-muted">{{ $t('grading.total') }}: {{ totalPrice.toLocaleString() }}</small>
                                        </div>
                                        <div class="text-end">
                                            <button class="btn btn-light text-dark" :disabled="selection.length === 0 || totalPrice > (summary?.credit ?? 0)" @click="submit">
                                                {{ $t('grading.submit') }}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="col-12 col-lg-4">
                            <div class="card bg-black text-white">
                                <div class="card-body">
                                    <h5 class="card-title">{{ $t('grading.infoTitle') }}</h5>
                                    <ul>
                                        <li>{{ $t('grading.infoPoint1') }}</li>
                                        <li>{{ $t('grading.infoPoint2') }}</li>
                                        <li>{{ $t('grading.infoPoint3') }}</li>
                                        <li>{{ $t('grading.infoPoint4') }}</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Selection Modal (reusing Trading-style offcanvas) -->
                    <div class="offcanvas offcanvas-end" id="grading-selection-canvas" tabindex="-1" role="dialog" aria-labelledby="grading-selection-canvas-label" aria-hidden="true">
                        <div class="offcanvas-header">
                            <h3 class="text-center text-dark w-100">{{ $t('grading.selectCards') }}</h3>
                        </div>
                        <div class="offcanvas-body">
                            <p class="alert alert-info">
                                {{ $t('grading.selectionInfo', {count: tempSelection.length, limit: remainingToday}) }}
                            </p>
                            <div class="row m-0">
                                <div class="col-4 pb-3" v-for="card in cards" :key="card.card_id">
                                    <a class="d-block" @click="selectOne(card)" :title="card.name">
                                        <img :src="CardService.getSmallCardImageUrl({card_id: card.card_id, set_id: card.set_id})" :alt="card.name+' thumbnail'" class="w-100">
                                    </a>
                                </div>
                            </div>
                            <div class="mt-2 d-flex justify-content-between">
                                <a href="javascript:" class="btn btn-danger" @click="closeSelectionModal()">{{ $t('grading.cancel') }}</a>
                                <a href="javascript:" class="btn btn-success" @click="confirmSelection()">{{ $t('grading.confirmSelection') }}</a>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Pending Tab -->
                <div class="col-12 px-0" v-if="tab === 'pending'">
                    <div class="row" v-if="summary?.pending?.length">
                        <div class="col-12 col-md-6 col-lg-4 mb-3" v-for="item in summary.pending" :key="item.id">
                            <div class="card bg-black text-white h-100">
                                <div class="card-body">
                                    <div class="d-flex align-items-center">
                                        <div class="me-2 rounded" :style="'width:64px;height:92px;background-size:cover;background-image:url('+CardService.getSmallCardImageUrl(item.card)+')'"></div>
                                        <div class="flex-grow-1">
                                            <div class="fw-bold">{{ item.card?.name ?? item.card_id }}</div>
                                            <small class="text-muted d-block mb-2">{{ $t('grading.completesAt') }} {{ item.completes_at }}</small>
                                            <button class="btn btn-sm btn-light text-dark" @click="expedite(item)" :disabled="(summary?.skip_tokens ?? 0) < 1">
                                                {{ $t('grading.finishNowToken') }}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="row" v-else>
                        <div class="col-12">
                            <div class="mt-2 alert alert-info">{{ $t('grading.noPending') }}</div>
                        </div>
                    </div>
                </div>

                <!-- Completed Tab -->
                <div class="col-12 px-0" v-if="tab === 'completed'">
                    <div class="row px-0" v-if="summary?.completed?.length">
                        <div class="col-6 col-sm-4 col-md-4 col-xl-3 graded-item" v-for="item in summary.completed" :key="item.id">
                            <div class="psa-slab">
                                <div class="psa-header">
                                    <div class="psa-grade">{{ item.grade?.grade ?? '?' }}</div>
                                    <div class="psa-label">{{ item.grade?.name ?? ('Grade '+item.grade_id) }}</div>
                                </div>
                                <div class="psa-body">
                                    <img :src="CardService.getSmallCardImageUrl(item.card)" :alt="item.card?.name ?? item.card_id" />
                                </div>
                                <div class="psa-footer">
                                    <div class="psa-name" v-text="item.card?.name ?? item.card_id"></div>
                                    <div class="psa-prices">
                                        <small class="text-muted">{{ $t('grading.basePrice') }}: {{ (item.base_price||0).toLocaleString() }}</small>
                                        <small class="text-muted">{{ $t('grading.gradedPrice') }}: {{ (item.final_price||0).toLocaleString() }}</small>
                                    </div>
                                    <small class="text-muted">{{ $t('grading.gradedAt') }} {{ item.graded_at }}</small>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="text-center text-muted py-5">{{ $t('grading.noCompleted') }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import CardService from "../../CardService";

export default {
    name: 'CardGrading',
    data() {
        return {
            tab: 'submit',
            summary: null,
            cards: [],
            selection: [],
            tempSelection: [],
        }
    },
    computed: {
        CardService() { return CardService },
        remainingToday() {
            return this.summary ? this.summary.remaining_today : 20;
        },
        totalPrice() {
            return this.cards.filter(c => this.selection.includes(c.card_id)).reduce((s, c) => s + (c.price || 0), 0);
        },
        selectedCards() {
            return this.cards.filter(c => this.selection.includes(c.card_id));
        }
    },
    methods: {
        async load() {
            const [summaryRes, cardsRes] = await Promise.all([
                axios.get('/api/grading/summary'),
                axios.get('/api/grading/cards'),
            ]);
            this.summary = summaryRes.data;
            this.cards = cardsRes.data.cards || [];
        },
        openSelectionModal() {
            this.tempSelection = [];
            $("#grading-selection-canvas").offcanvas('show');
        },
        closeSelectionModal() {
            $("#grading-selection-canvas").offcanvas('hide');
        },
        toggleTemp(cardId) {
            // no longer used in single-select mode
        },
        selectOne(card) {
            if (this.selection.length >= this.remainingToday) return;
            const cardId = card.card_id;
            if (!this.selection.includes(cardId)) {
                this.selection.push(cardId);
            }
            this.closeSelectionModal();
        },
        confirmSelection() {
            // no longer needed in single-select mode, but keep for safety
            this.closeSelectionModal();
        },
        removeFromSelection(cardId) {
            this.selection = this.selection.filter(id => id !== cardId);
        },
        async submit() {
            if (!this.selection.length) return;
            try {
                const res = await axios.post('/api/grading/submit', {cards: this.selection});
                if (res.data?.success) {
                    this.selection = [];
                    await this.load();
                    this.tab = 'pending';
                }
            } catch (e) {
                alert(e?.response?.data?.message ?? 'Submission failed');
            }
        },
        async expedite(item) {
            try {
                const res = await axios.post('/api/grading/expedite', {id: item.id});
                if (res.data?.success) {
                    await this.load();
                    this.tab = 'completed';
                }
            } catch (e) {
                alert(e?.response?.data?.message ?? 'Failed to finish now');
            }
        }
    },
    mounted() {
        this.load();
    }
}
</script>

<style scoped>
.page-heading {
    margin-top: 12px;
}

/* PSA-like slab frame */
.psa-slab {
    background: #f7f7f7;
    border: 4px solid #d9d9d9;
    border-radius: 10px;
    box-shadow: 0 4px 14px rgba(0,0,0,0.35);
    overflow: hidden;
}
.psa-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 6px;
    background: linear-gradient(180deg, #c8102e 0%, #a40d25 100%);
    color: #fff;
    font-weight: 700;
    letter-spacing: 0.3px;
}
.psa-grade {
    font-size: 1.4em;
    line-height: 1;
}
.psa-label {
    font-size: 0.85em;
    opacity: 0.95;
}
.psa-body {
    background: #e9e9e9;
    padding: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
}
.psa-body img {
    width: 100%;
    height: auto;
    border-radius: 6px;
    box-shadow: 0 2px 6px rgba(0,0,0,0.25);
}
.psa-footer {
    padding: 6px;
    background: #f5f5f5;
    border-top: 1px solid #e1e1e1;
}
.psa-name {
    font-weight: 600;
    font-size: 0.95em;
    margin-bottom: 2px;
}
.psa-prices {
    display: flex;
    flex-direction: column;
}

/* Add some vertical spacing between cards within the grading component */
.card {
    margin-top: 4px;
    margin-bottom: 4px;
}

/* Also add a slight margin to PSA slabs to create breathing room in the grid */
.psa-slab {
    margin-top: 4px;
    margin-bottom: 4px;
}

/* Align card areas with tabs */
.container .nav-tabs + * {
    margin-top: 6px;
}

/* Remove horizontal padding so content aligns with tabs edges */
.col-12.px-0 {
    padding-left: 0 !important;
    padding-right: 0 !important;
}
</style>
